import React, {useEffect, useState, useMemo} from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import {Button} from '@thryvlabs/maverick'
import {useDispatch, useSelector} from 'react-redux'
import {
  selectProductGroups,
  selectLoading,
  selectError,
} from '../../app-store/productRules/productRulesSlice'
import {
  fetchProductGroups,
  createProductGroup,
  deleteProductGroup,
  addPlanCodeThunk,
  updatePlanCodeThunk,
  deletePlanCodeThunk,
} from '../../app-store/productRules/productRulesThunk'
import {useAuthClient} from '../../utils/use-auth-client'
import SharedModal from '../shared/Modal'
import {
  AddGroupModal,
  AddCodeModal,
  EditCodeModal,
  RemoveCodeModal,
  DeleteGroupModal,
} from './ProductGroupModals'

const thS = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#6B7280',
  background: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB',
  userSelect: 'none',
}
const tdS = {
  padding: '8px 12px',
  fontSize: 12,
  color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  verticalAlign: 'middle',
}

const groupByProductGrp = rows =>
  (rows || []).reduce((acc, {product_group, plan_code}) => {
    if (!acc[product_group]) acc[product_group] = []
    acc[product_group].push(plan_code)
    return acc
  }, {})

function GroupSkeleton() {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: 10,
        overflow: 'hidden',
        padding: '12px 16px',
      }}
    >
      <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
        <Skeleton circle width={12} height={12} />
        <Skeleton width={160} height={14} />
        <Skeleton width={80} height={12} style={{marginLeft: 'auto'}} />
        <Skeleton width={70} height={26} borderRadius={4} />
        <Skeleton width={90} height={26} borderRadius={4} />
      </div>
    </div>
  )
}

export default function ManageProducts() {
  const dispatch = useDispatch()
  const client = useAuthClient()

  const productGroupRows = useSelector(selectProductGroups)
  const loading = useSelector(selectLoading)
  const error = useSelector(selectError)

  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState({})

  // modal state: null | { type: 'addGroup' | 'addCode' | 'editCode' | 'removeCode' | 'deleteGroup', ...data }
  const [modal, setModal] = useState(null)
  const closeModal = () => setModal(null)

  useEffect(() => {
    if (!Array.isArray(productGroupRows) || productGroupRows.length === 0) {
      if (!loading) dispatch(fetchProductGroups({client}))
    }
  }, [dispatch, client, productGroupRows, loading])

  const products = useMemo(
    () => groupByProductGrp(productGroupRows),
    [productGroupRows],
  )
  const toggleExpand = g => setExpanded(e => ({...e, [g]: !e[g]}))

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return q
      ? Object.entries(products).filter(
          ([grp, codes]) =>
            grp.toLowerCase().includes(q) ||
            codes.some(c => c.toLowerCase().includes(q)),
        )
      : Object.entries(products)
  }, [products, search])

  const handleAddGroup = (name, codes) => {
    dispatch(createProductGroup({client, payload: {name, planCodes: codes}}))
    closeModal()
  }
  const handleDeleteGroup = group => {
    dispatch(deleteProductGroup({client, groupName: group}))
    closeModal()
  }
  const handleAddCode = (group, code) => {
    dispatch(addPlanCodeThunk({client, groupName: group, code}))
    closeModal()
  }
  const handleEditCode = (group, oldCode, newCode) => {
    dispatch(updatePlanCodeThunk({client, groupName: group, oldCode, newCode}))
    closeModal()
  }
  const handleRemoveCode = (group, code) => {
    dispatch(deletePlanCodeThunk({client, groupName: group, code}))
    closeModal()
  }

  if (error) {
    return (
      <div style={{padding: '1.5rem'}}>
        <div
          style={{
            padding: '12px 16px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            fontSize: 13,
            color: '#991B1B',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Failed to load product groups: {error}</span>
          <Button
            variant="primary"
            onClick={() => dispatch(fetchProductGroups({client}))}
          >
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div style={{padding: '1.5rem'}}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
        }}
      >
        <p style={{margin: 0, fontSize: 13, color: '#9CA3AF'}}>
          View and manage product groups and their plan codes
        </p>

        <button
          onClick={() => setModal({type: 'addGroup'})}
          style={{
            padding: '8px 16px',
            background: '#ff5000',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          + New product group
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: '1rem',
        }}
      >
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search groups or plan codes…"
          style={{
            padding: '7px 10px',
            fontSize: 13,
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            outline: 'none',
            fontFamily: 'inherit',
            background: '#fff',
            color: '#111',
            boxSizing: 'border-box',
            width: 260,
          }}
          disabled={loading}
        />
      </div>

      <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
        {loading ? (
          Array.from({length: 2}).map((_, i) => <GroupSkeleton key={i} />)
        ) : (
          <>
            {filtered.map(([group, codes]) => (
              <div
                key={group}
                style={{
                  background: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: 10,
                  overflow: 'hidden',
                }}
              >
                <div
                  onClick={() => toggleExpand(group)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      color: '#9CA3AF',
                      width: 12,
                      textAlign: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {expanded[group] ? '▼' : '▶'}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#111',
                      flex: 1,
                    }}
                  >
                    {group}
                  </span>

                  <span onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setModal({type: 'addCode', group, codes})}
                      style={{
                        fontSize: 11,
                        padding: '3px 9px',
                        border: '1px solid #D1D5DB',
                        borderRadius: 4,
                        background: '#fff',
                        cursor: 'pointer',
                        color: '#374151',
                        marginRight: 6,
                      }}
                    >
                      + Add code
                    </button>
                  </span>

                  <span onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setModal({type: 'deleteGroup', group})}
                      style={{
                        fontSize: 11,
                        padding: '3px 9px',
                        border: '1px solid #FECACA',
                        borderRadius: 4,
                        background: '#fff',
                        cursor: 'pointer',
                        color: '#DC2626',
                      }}
                    >
                      Delete group
                    </button>
                  </span>
                </div>

                {expanded[group] && (
                  <div style={{borderTop: '1px solid #E5E7EB'}}>
                    {codes.length === 0 ? (
                      <p
                        style={{
                          margin: 0,
                          padding: '1rem 16px',
                          fontSize: 13,
                          color: '#9CA3AF',
                        }}
                      >
                        No plan codes. Click {'+ Add code'} to add one.
                      </p>
                    ) : (
                      <table
                        style={{width: '100%', borderCollapse: 'collapse'}}
                      >
                        <thead>
                          <tr>
                            <th style={{...thS, width: 36}}>#</th>
                            <th style={thS}>Plan code</th>
                            <th style={{...thS, textAlign: 'right'}}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {codes.map((code, i) => (
                            <tr
                              key={code}
                              style={{
                                background: i % 2 === 0 ? '#fff' : '#FAFAFA',
                              }}
                            >
                              <td style={{...tdS, color: '#9CA3AF', width: 36}}>
                                {i + 1}
                              </td>
                              <td
                                style={{
                                  ...tdS,
                                  fontFamily: 'monospace',
                                  fontSize: 12,
                                }}
                              >
                                {code}
                              </td>
                              <td
                                style={{
                                  ...tdS,
                                  textAlign: 'right',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <button
                                  onClick={() =>
                                    setModal({
                                      type: 'editCode',
                                      group,
                                      code,
                                      codes,
                                    })
                                  }
                                  style={{
                                    fontSize: 11,
                                    padding: '3px 9px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: 4,
                                    background: '#fff',
                                    cursor: 'pointer',
                                    color: '#374151',
                                    marginRight: 6,
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() =>
                                    setModal({
                                      type: 'removeCode',
                                      group,
                                      code,
                                      codes,
                                    })
                                  }
                                  style={{
                                    fontSize: 11,
                                    padding: '3px 9px',
                                    border: '1px solid #FECACA',
                                    borderRadius: 4,
                                    background: '#fff',
                                    cursor: 'pointer',
                                    color: '#DC2626',
                                  }}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}

            {filtered.length === 0 && (
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: 10,
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: 13,
                }}
              >
                No product groups match your search.
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modals ── */}
      {modal?.type === 'addGroup' && (
        <SharedModal title="New product group" onClose={closeModal}>
          <AddGroupModal
            existingGroups={Object.keys(products)}
            onClose={closeModal}
            onSave={handleAddGroup}
          />
        </SharedModal>
      )}

      {modal?.type === 'addCode' && (
        <SharedModal title="Add plan code" onClose={closeModal}>
          <AddCodeModal
            group={modal.group}
            existingCodes={modal.codes}
            onClose={closeModal}
            onSave={code => handleAddCode(modal.group, code)}
          />
        </SharedModal>
      )}

      {modal?.type === 'editCode' && (
        <SharedModal title="Edit plan code" onClose={closeModal}>
          <EditCodeModal
            group={modal.group}
            code={modal.code}
            existingCodes={modal.codes}
            onClose={closeModal}
            onSave={newCode => handleEditCode(modal.group, modal.code, newCode)}
          />
        </SharedModal>
      )}

      {modal?.type === 'removeCode' && (
        <SharedModal
          title={
            modal.codes.length === 1
              ? 'Remove plan code & group?'
              : 'Remove plan code'
          }
          onClose={closeModal}
        >
          <RemoveCodeModal
            group={modal.group}
            code={modal.code}
            isLast={modal.codes.length === 1}
            onClose={closeModal}
            onConfirm={() => handleRemoveCode(modal.group, modal.code)}
          />
        </SharedModal>
      )}

      {modal?.type === 'deleteGroup' && (
        <SharedModal title="Delete product group" onClose={closeModal}>
          <DeleteGroupModal
            group={modal.group}
            onClose={closeModal}
            onConfirm={() => handleDeleteGroup(modal.group)}
          />
        </SharedModal>
      )}
    </div>
  )
}
